import { reactive, readonly } from 'vue'
import { projectFirestore, projectFunctions } from '@/firebase/config'

const state = reactive({
  characterStatsData: null,
  dailyCharacterStatsData: null,
  weeklyCharacterStatsData: null,
  loading: false
})

const readonlyState = readonly(state)

const ONE_HOUR = 60 * 60 * 1000

export function useMissionStatsStore () {
  const fetchIfNeeded = async (userData, uid) => {
    const userId = uid || userData?.uid
    if (!userId) return

    const lastFetch = userData?.lastMissionStatsFetch
    const now = Date.now()

    if (lastFetch) {
      const lastFetchMs = lastFetch.toMillis ? lastFetch.toMillis() : new Date(lastFetch).getTime()
      if (now - lastFetchMs < ONE_HOUR && state.characterStatsData !== null) {
        return
      }
    }

    state.loading = true
    try {
      const [charStatsDoc, dailyDoc, weeklyDoc] = await Promise.all([
        projectFirestore.collection('characterStats').doc(userId).get(),
        projectFirestore.collection('dailyCharacterStats').doc(userId).get(),
        projectFirestore.collection('weeklyCharacterStats').doc(userId).get()
      ])

      state.characterStatsData = charStatsDoc.exists ? charStatsDoc.data() : {
        zombiesKilled: 0, explorationsByMap: {}, zombiesKilledByName: {}, criminalsKilledByName: {},
        decomposedItems: 0, decomposedItemsByName: {}, craftedItemsByName: {}, craftedItems: 0, totalSkillsLearned: 0,
        tattoosApplied: 0, silverFromItemSales: 0, mapsExplored: 0, roomUpgrades: 0,
        criminalsKilled: 0, survivorsKilled: 0,
      }
      state.dailyCharacterStatsData = dailyDoc.exists ? dailyDoc.data() : {}
      state.weeklyCharacterStatsData = weeklyDoc.exists ? weeklyDoc.data() : {}

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions')
        await gameActions({ action: 'updateMissionStatsFetchTime' })
      } catch (err) {
        console.error('[useMissionStatsStore] updateMissionStatsFetchTime error:', err)
      }
    } catch (err) {
      console.error('[useMissionStatsStore] fetch error:', err)
    } finally {
      state.loading = false
    }
  }

  const fetch = async (uid) => {
    if (!uid) return
    state.loading = true
    try {
      const [charStatsDoc, dailyDoc, weeklyDoc] = await Promise.all([
        projectFirestore.collection('characterStats').doc(uid).get(),
        projectFirestore.collection('dailyCharacterStats').doc(uid).get(),
        projectFirestore.collection('weeklyCharacterStats').doc(uid).get()
      ])
      state.characterStatsData = charStatsDoc.exists ? charStatsDoc.data() : {
        zombiesKilled: 0, explorationsByMap: {}, zombiesKilledByName: {}, criminalsKilledByName: {},
        decomposedItems: 0, decomposedItemsByName: {}, craftedItemsByName: {}, craftedItems: 0, totalSkillsLearned: 0,
        tattoosApplied: 0, silverFromItemSales: 0, mapsExplored: 0, roomUpgrades: 0,
        criminalsKilled: 0, survivorsKilled: 0,
      }
      state.dailyCharacterStatsData = dailyDoc.exists ? dailyDoc.data() : {}
      state.weeklyCharacterStatsData = weeklyDoc.exists ? weeklyDoc.data() : {}
    } catch (err) {
      console.error('[useMissionStatsStore] fetch error:', err)
    } finally {
      state.loading = false
    }
  }

  const invalidate = () => {
    state.characterStatsData = null
    state.dailyCharacterStatsData = null
    state.weeklyCharacterStatsData = null
    state.loading = false
  }

  return {
    get characterStatsData () { return readonlyState.characterStatsData },
    get dailyCharacterStatsData () { return readonlyState.dailyCharacterStatsData },
    get weeklyCharacterStatsData () { return readonlyState.weeklyCharacterStatsData },
    get loading () { return readonlyState.loading },
    fetchIfNeeded,
    fetch,
    invalidate
  }
}
